/**
 * Formats traffic update description text with proper styling:
 * - Underlines cancelled stops title (תחנות מבוטלות/תחנה מבוטלת) and alternative stops title (תחנות חלופיות/תחנה חלופית)
 * - Makes route titles bold with paragraph break before them (קו/קווי followed by :)
 * - Preserves the rest of the text as-is
 */
export function formatTrafficDescription(text: string): string {
  if (!text) return ""

  // Split text into paragraphs (by double newline or single newline)
  const paragraphs = text.split(/\n+/)

  const formattedParagraphs = paragraphs.map((paragraph, index) => {
    const trimmedParagraph = paragraph.trim()
    if (!trimmedParagraph) return ""

    // Check for cancelled stops patterns (underline just the title)
    // Matches: תחנות מבוטלות זמנית:, תחנה מבוטלת זמנית:, תחנות מבוטלות:, תחנה מבוטלת:
    const cancelledStopsPattern = /^(תחנ(?:ות|ה)\s+מבוטל(?:ות|ת)\s*(?:זמנית)?:)/

    // Check for alternative stops patterns (underline just the title)
    // Matches: תחנות חלופיות:, תחנה חלופית:
    const alternativeStopsPattern = /^(תחנ(?:ות|ה)\s+חלופי(?:ות|ת):)/

    // Check for route patterns (bold with paragraph break before)
    // Matches: קו 74 מ... :, קווי הלילה 296, 489 מ... :, קו לילה 425 מ... :
    const routePattern = /^(קו(?:וי)?\s+(?:הלילה\s+|לילה\s+)?[^:]+:)/

    if (cancelledStopsPattern.test(trimmedParagraph)) {
      // Underline only the title part (up to and including colon)
      return trimmedParagraph.replace(cancelledStopsPattern, "<u>$1</u>")
    } else if (alternativeStopsPattern.test(trimmedParagraph)) {
      // Underline only the title part (up to and including colon)
      return trimmedParagraph.replace(alternativeStopsPattern, "<u>$1</u>")
    } else if (routePattern.test(trimmedParagraph)) {
      // Bold the route title part (everything up to and including the colon)
      // Add paragraph break before (but not for the first paragraph)
      const formattedText = trimmedParagraph.replace(
        routePattern,
        "<strong>$1</strong><br/>",
      )
      return index > 0 ? `<p></p>${formattedText}` : formattedText
    }

    return trimmedParagraph
  })

  // Join paragraphs with line breaks
  return formattedParagraphs.filter((p) => p).join("<br/>")
}
